import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { authApi } from '../api/auth.js';
import { ApiClientError } from '../api/client.js';
import { useAuth } from '../context/AuthContext.js';
import './LoginPage.css';

export function LoginPage() {
  const { t } = useTranslation();
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (response) => {
      login(response.token);
      const from = (location.state as { from?: Location } | null)?.from;
      navigate(from ? `${from.pathname}${from.search}` : '/feed', { replace: true });
    },
  });

  if (isAuthenticated) return <Navigate to="/" replace />;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    loginMutation.mutate({ username, password });
  }

  const errorMessage =
    loginMutation.error instanceof ApiClientError
      ? t(`errors.${loginMutation.error.code}`, { defaultValue: t('errors.internal_error') })
      : null;

  return (
    <div className="login-page">
      <form className="login-card card" onSubmit={handleSubmit}>
        <h1>{t('auth.title')}</h1>
        <p className="login-subtitle">{t('auth.subtitle')}</p>

        <div className="field">
          <label htmlFor="login-username">{t('auth.username')}</label>
          <input
            id="login-username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </div>
        <div className="field">
          <label htmlFor="login-password">{t('auth.password')}</label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {errorMessage && <p className="field-error">{errorMessage}</p>}

        <button
          type="submit"
          className="btn btn-primary login-submit"
          disabled={loginMutation.isPending}
        >
          {t('auth.submit')}
        </button>
      </form>
    </div>
  );
}
